/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Heading, Image} from '@salesforce/retail-react-app/app/components/shared/ui'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.ITCText - Text Below Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @returns {React.ReactElement} - HeroBanner component.
 */
export const CalloutTiles = ({
    heading,
    headingTextFontWeight,
    subTextStyle,
    subTextFontWeight,
    alt,
    headingTextStyle,
    headingTextColor,
    subTextColor,
    subText,
    custom
}) => {
    const segment = useSegment()

    const sanitizeHeading = (html) => {
        // Remove outer p tags but keep inner content and other valid tags
        return html.replace(/^<p>(.*)<\/p>$/s, '$1')
    }

    return (
        <Box
            className={'callout-tile-container'}
            onClick={(event) => {
                // segment
                segment.sendPanelSelectedEvent(event, {}, {})
            }}
        >
            <Box
                className={'callout-tile-component'}
                maxW={'330px'}
                w={{base: '100%', md: '95%', lg: '70%'}}
                m={'0 auto'}
                textAlign={'center'}
            >
                <Image src={custom?.image?.disBaseLink} alt={alt} loading="lazy" mx="auto" />

                <Box>
                    {heading && (
                        <Heading
                            as="h3"
                            fontSize={{base: '20px', lg: '24px'}}
                            lineHeight={{base: '28px', lg: '32px'}}
                            margin={'16px 0 10px'}
                            fontStyle={headingTextStyle || ''}
                            fontWeight={headingTextFontWeight || ''}
                            color={(headingTextColor && headingTextColor.value) || ''}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHeading(heading)
                            }}
                        ></Heading>
                    )}
                    {subText && (
                        <Text
                            fontStyle={subTextStyle || ''}
                            fontWeight={subTextFontWeight || 'normal'}
                            h={{base: 'auto', sm: '98px'}}
                            mb={1}
                            noOfLines={4}
                            color={(subTextColor && subTextColor.value) || ''}
                            dangerouslySetInnerHTML={{__html: sanitizeHeading(subText || '')}}
                            suppressHydrationWarning
                        />
                    )}
                </Box>
            </Box>
        </Box>
    )
}

CalloutTiles.propTypes = {
    heading: PropTypes.string,
    headingTextFontWeight: PropTypes.string,
    subTextStyle: PropTypes.string,
    subTextFontWeight: PropTypes.string,
    alt: PropTypes.string,
    headingTextStyle: PropTypes.string,
    subTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    headingTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    subText: PropTypes.string,
    custom: PropTypes.object
}

export default CalloutTiles
